import { useRef, useState, useEffect } from 'react';
import { X, Play, Pause, Video, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { formatDuration, formatFileSize } from '@/utils/videoValidation';

interface VideoUploadPreviewProps {
  file: File;
  videoUrl: string;
  duration: number;
  onRemove: () => void;
  disabled?: boolean;
  className?: string;
}

export function VideoUploadPreview({
  file,
  videoUrl,
  duration,
  onRemove,
  disabled = false,
  className,
}: VideoUploadPreviewProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Auto-play muted when loaded
    if (videoRef.current && isLoaded) {
      videoRef.current.play().catch(() => {
        // Autoplay blocked, that's fine
        setIsPlaying(false);
      });
    }
  }, [isLoaded]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      className={cn(
        "relative rounded-2xl overflow-hidden bg-black",
        className
      )}
    >
      {/* Video Player */}
      <video
        ref={videoRef}
        src={videoUrl}
        className="w-full aspect-[3/4] object-cover"
        loop
        playsInline
        onLoadedData={() => setIsLoaded(true)}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />

      {/* Loading Overlay */}
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted">
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
        </div>
      )}

      {/* Play/Pause Button */}
      <button
        onClick={togglePlay}
        disabled={!isLoaded || disabled}
        className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-0 hover:opacity-100 transition-opacity"
      >
        <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center shadow-lg">
          {isPlaying ? (
            <Pause className="w-6 h-6 text-black" />
          ) : (
            <Play className="w-6 h-6 text-black ml-1" />
          )}
        </div>
      </button>

      {/* Duration Badge */}
      <div className="absolute bottom-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
        <Video className="w-3.5 h-3.5 text-white" />
        <span className="text-xs font-medium text-white">
          {formatDuration(duration)}
        </span>
      </div>

      {/* File Size Badge */}
      <div className="absolute bottom-3 right-3 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
        <span className="text-xs font-medium text-white">
          {formatFileSize(file.size)}
        </span>
      </div>

      {/* Remove Button */}
      <button
        onClick={onRemove}
        disabled={disabled}
        className="absolute top-2 right-2 w-8 h-8 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center shadow-lg hover:bg-destructive/90 transition-colors disabled:opacity-50"
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
}
